import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";
import {
  FLOOR_PAGE_SCAN_TARGET_PROPS,
  useFloorPageScanCapture,
} from "@/lib/floorPageScanCapture";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

import {
  applyInventoryLocationChange,
  fetchInventoryLocationChangeContext,
} from "./inventoryLocationChangeApi";
import { KITTING_EMPTY_SUBLOCATION_STOP } from "./kittingPickModel";
import type { KittingPickInventorySelection } from "./kittingPickInventorySelection";

function parsePositiveQuantityDraft(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.round(parsed);
}

export function KittingInventoryLocationChangeModal({
  open,
  selection,
  knownStorageLocations,
  allowChangeSource = false,
  onClose,
}: {
  open: boolean;
  selection: KittingPickInventorySelection | null;
  knownStorageLocations: string[];
  /** When true, current sublocation is a picker (SKU inventory tab with multiple bins). */
  allowChangeSource?: boolean;
  onClose: () => void;
}): JSX.Element | null {
  const canWrite = useHubModuleWrite("manufacturing");
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [fromSublocation, setFromSublocation] = useState("");
  const [requestedSublocation, setRequestedSublocation] = useState("");
  const [quantityDraft, setQuantityDraft] = useState("");
  const destinationInputRef = useRef<HTMLInputElement>(null);
  const sourceLocation =
    fromSublocation.trim().length > 0
      ? fromSublocation.trim()
      : (selection?.storageLocation.trim() ?? "");
  useFloorPageScanCapture({
    enabled: open && selection != null,
    inputRef: destinationInputRef,
    onScan: (serial) => setRequestedSublocation(serial),
  });

  const contextQuery = useQuery({
    queryKey: [
      "manufacturing",
      "inventory-location-change",
      "context",
      selection?.sku ?? "",
      sourceLocation,
    ],
    queryFn: () => {
      if (selection == null) {
        throw new Error("Select a SKU and storage location first.");
      }
      return fetchInventoryLocationChangeContext({
        sku_id: selection.sku,
        from_sublocation: sourceLocation,
      });
    },
    enabled: open && selection != null && sourceLocation.length > 0,
  });

  const quantityAtSource = contextQuery.data?.quantity_at_source ?? 0;
  const unattributedQuantity = contextQuery.data?.unattributed_quantity ?? 0;
  const fromIsUnbucketed =
    sourceLocation.toLowerCase() === KITTING_EMPTY_SUBLOCATION_STOP.toLowerCase();

  useEffect(() => {
    if (!open || selection == null) return;
    setFromSublocation(selection.storageLocation);
    setRequestedSublocation("");
    setQuantityDraft("");
  }, [open, selection?.sku, selection?.storageLocation]);

  useEffect(() => {
    if (!open || quantityAtSource <= 0) return;
    setQuantityDraft(String(quantityAtSource));
  }, [open, quantityAtSource, sourceLocation]);

  const parsedQuantity = useMemo(() => parsePositiveQuantityDraft(quantityDraft), [quantityDraft]);

  const submitMutation = useMutation({
    mutationFn: () => {
      if (selection == null) {
        throw new Error("Select a SKU and storage location first.");
      }
      const next = requestedSublocation.trim();
      if (next === "") {
        throw new Error("Enter the destination sublocation.");
      }
      if (next.toLowerCase() === sourceLocation.toLowerCase()) {
        throw new Error("Destination sublocation must differ from the current one.");
      }
      if (parsedQuantity == null) {
        throw new Error("Enter a positive quantity to move.");
      }
      if (parsedQuantity > quantityAtSource) {
        throw new Error(`Quantity cannot exceed ${String(quantityAtSource)} on hand at the source.`);
      }
      return applyInventoryLocationChange({
        sku_id: selection.sku.trim(),
        from_sublocation: sourceLocation,
        to_sublocation: next,
        quantity: parsedQuantity,
      });
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "kitting", "pick-data"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "inventory-sublocations"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "inventory"] });
      const moved = result.move;
      showToast({
        tone: "success",
        message: `Moved ${String(parsedQuantity ?? "")} to ${inventoryLocationLabel(moved.to.sublocation)} (${String(moved.from.quantity)} remain at source).`,
      });
      onClose();
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  const sourceLocationOptions = useMemo(() => {
    const labels = [...knownStorageLocations];
    if (
      sourceLocation.length > 0 &&
      !labels.some((location) => location.toLowerCase() === sourceLocation.toLowerCase())
    ) {
      labels.unshift(sourceLocation);
    }
    return labels;
  }, [knownStorageLocations, sourceLocation]);

  if (!open || selection == null) return null;

  const datalistId = "kitting-location-change-sublocations";
  const submitDisabled =
    !canWrite ||
    requestedSublocation.trim() === "" ||
    parsedQuantity == null ||
    quantityAtSource <= 0 ||
    contextQuery.isLoading ||
    submitMutation.isPending;

  return (
    <div
      className="fixed inset-0 z-[55] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kitting-location-change-title"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
        <h2 id="kitting-location-change-title" className="text-lg font-semibold text-slate-900">
          Move inventory between sublocations
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Transfer quantity from the current bin to another floor location. Leave part of the stock
          behind when only some units are moving.
        </p>
        <div className="mt-4 space-y-3">
          <div>
            <Label htmlFor="kitting-location-change-sku">SKU</Label>
            <Input id="kitting-location-change-sku" value={selection.sku} readOnly className="font-mono" />
          </div>
          <div>
            <Label htmlFor="kitting-location-change-current">Current sublocation</Label>
            {allowChangeSource ? (
              <select
                id="kitting-location-change-current"
                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 font-mono text-sm text-slate-900"
                value={sourceLocation}
                onChange={(event) => setFromSublocation(event.target.value)}
              >
                {sourceLocationOptions.map((location) => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            ) : (
              <Input
                id="kitting-location-change-current"
                value={sourceLocation}
                readOnly
                className="font-mono"
              />
            )}
          </div>
          <div>
            <Label htmlFor="kitting-location-change-on-hand">On hand at current sublocation</Label>
            <Input
              id="kitting-location-change-on-hand"
              value={contextQuery.isLoading ? "Loading…" : String(quantityAtSource)}
              readOnly
              className="font-mono"
            />
          </div>
          {fromIsUnbucketed && unattributedQuantity > 0 ? (
            <div>
              <Label htmlFor="kitting-location-change-unattributed">Unattributed at SGS</Label>
              <Input
                id="kitting-location-change-unattributed"
                value={String(unattributedQuantity)}
                readOnly
                className="font-mono"
              />
              <p className="mt-1 text-xs text-slate-500">
                Snapshot on-hand not yet assigned to a hub bin. Moving from No sub location uses this
                balance when no hub row exists yet.
              </p>
            </div>
          ) : null}
          <div>
            <Label htmlFor="kitting-location-change-quantity">Quantity to move</Label>
            <Input
              id="kitting-location-change-quantity"
              inputMode="numeric"
              value={quantityDraft}
              onChange={(event) => setQuantityDraft(event.target.value)}
              placeholder={quantityAtSource > 0 ? String(quantityAtSource) : "0"}
              className="font-mono"
            />
          </div>
          <div>
            <Label htmlFor="kitting-location-change-next">Destination sublocation</Label>
            <Input
              id="kitting-location-change-next"
              ref={destinationInputRef}
              list={datalistId}
              value={requestedSublocation}
              onChange={(event) => setRequestedSublocation(event.target.value)}
              placeholder="Scan or type the bin / aisle code…"
              className="font-mono"
              {...FLOOR_PAGE_SCAN_TARGET_PROPS}
            />
            <datalist id={datalistId}>
              {knownStorageLocations
                .filter((location) => location.toLowerCase() !== sourceLocation.toLowerCase())
                .map((location) => (
                  <option key={location} value={location} />
                ))}
            </datalist>
          </div>
          {contextQuery.error ? (
            <p className="text-sm text-rose-700">{(contextQuery.error as Error).message}</p>
          ) : null}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={submitDisabled}
            isLoading={submitMutation.isPending}
            onClick={() => submitMutation.mutate()}
          >
            Move stock
          </Button>
        </div>
      </div>
    </div>
  );
}

function inventoryLocationLabel(raw: string): string {
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : KITTING_EMPTY_SUBLOCATION_STOP;
}
